import React, {ChangeEvent} from "react";
import {LibrarySimpleDTO} from "../../../models/library";
import {Box, Button, Grid, InputAdornment, TextField} from "@material-ui/core";
import {createStyles, makeStyles, Theme} from "@material-ui/core/styles";
import {useTranslation} from "react-i18next";
import {compareByAttr} from "../../../utils/compare";
import {Alert} from "@material-ui/lab";
import SearchIcon from "@material-ui/icons/Search";
import TuneIcon from "@material-ui/icons/Tune";

interface LibrariesListProps {
    libraries: LibrarySimpleDTO[];
    children: (library: LibrarySimpleDTO) => React.ReactElement<HTMLElement>;
}

interface LibrariesListState {
    filter: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        searchRow: {
            marginBottom: theme.spacing(3),
            display: "flex",
            gap: theme.spacing(1.5),
            alignItems: "center",
        },
        searchInput: {
            backgroundColor: "#ffffff",
            borderRadius: "8px",
            flexGrow: 1,
        },
        filtersButton: {
            flexShrink: 0,
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 600,
            color: "#374151",
            borderColor: "#E5E7EB",
            backgroundColor: "#ffffff",
            whiteSpace: "nowrap",
        },
        grid: {
            marginTop: 0,
        },
    })
);

function libraryFilter(filter: string) {
    const lc = filter.toLowerCase();
    return (library: LibrarySimpleDTO): boolean => {
        return library.name.toLowerCase().includes(lc);
    };
}

export const LibrariesList: React.FC<LibrariesListProps> = (props: LibrariesListProps) => {
    const initState: LibrariesListState = {
        filter: "",
    };
    const [state, setState] = React.useState(initState);
    const classes = useStyles();
    const [t] = useTranslation("libraries");
    const comparator = compareByAttr("name", "ascending");

    const handleFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
        setState({...state, filter: event.target.value});
    };

    const nameFilter = libraryFilter(state.filter);
    const libraries = props.libraries.filter(nameFilter).sort(comparator);

    return (
        <Box>
            <Box className={classes.searchRow}>
                <TextField
                    placeholder={t("library.list.filter")}
                    value={state.filter}
                    onChange={handleFilterChange}
                    variant="outlined"
                    size="small"
                    className={classes.searchInput}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon style={{color: "#9CA3AF", fontSize: "1.1rem"}} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    variant="outlined"
                    className={classes.filtersButton}
                    startIcon={<TuneIcon style={{fontSize: "1rem"}} />}
                >
                    Filters
                </Button>
            </Box>
            {libraries.length === 0 && <Alert severity="info">{t("library.list.empty")}</Alert>}
            <Grid container spacing={2} className={classes.grid}>
                {libraries.map((library) => (
                    <Grid item xs={12} sm={6} md={4} key={library.id}>
                        {props.children(library)}
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};
